/**
 * Clock, camera, and skeleton math for the live trial stage.
 * Pose samples are already in the display frame: (x, z, −y) with the
 * stance-heel plane on z = 0. Z is up. A non-finite joint is missing.
 */

export const LIVE_FPS = 30;
export const VERTICAL_FOV_DEG = 38;
export const CAMERA_NEAR = 0.4;
export const CAMERA_FAR = 30;
export const LOOK_AT_Z = 0.85;
export const DEFAULT_AZIMUTH = Math.atan2(-0.6, 1);
export const DEFAULT_ELEVATION = 0.34;
export const FLOOR_Z = -0.02;
export const FLOOR_CELL_M = 0.55;
export const FLOOR_COLOR_A = [0.82, 0.82, 0.84];
export const FLOOR_COLOR_B = [0.92, 0.92, 0.94];
export const BONE_THICKNESS_CSS_PX = 1.25;
export const JOINT_DIAMETER_CSS_PX = 7;
export const JOINT_RADIUS_CSS_PX = JOINT_DIAMETER_CSS_PX / 2;
export const DEPTH_PULL_NDC = 0.002;
export const JOINT_BASE_COLOR = [0.76, 0.78, 0.81];
export const JOINT_SPECULAR_POWER = 48;

/** tab10 with R and B swapped: cv2.line reads matplotlib RGBA bytes as BGR. */
export const TAB10_DISPLAYED_BYTES = [
  [180, 119, 31],
  [14, 127, 255],
  [44, 160, 44],
  [40, 39, 214],
  [189, 103, 148],
  [75, 86, 140],
  [194, 119, 227],
  [127, 127, 127],
  [34, 189, 188],
  [207, 190, 23],
];

export const SCHEME_CHAINS = [
  ['shoulder_left', 'elbow_left', 'wrist_left'],
  ['shoulder_right', 'elbow_right', 'wrist_right'],
  ['hip_left', 'knee_left', 'ankle_left'],
  ['hip_right', 'knee_right', 'ankle_right'],
  ['heel_left', 'big_toe_left', 'small_toe_left', 'heel_left', 'ankle_left'],
  ['heel_right', 'big_toe_right', 'small_toe_right', 'heel_right', 'ankle_right'],
  ['hip_left', 'hip_right'],
  ['shoulder_left', 'hip_left'],
  ['shoulder_right', 'hip_right'],
  ['shoulder_left', 'shoulder_right'],
  ['nose', 'ear_left', 'ear_right', 'nose'],
];

export function displayedTab10(chainIndex) {
  const n = TAB10_DISPLAYED_BYTES.length;
  const slot = ((chainIndex % n) + n) % n;
  const bytes = TAB10_DISPLAYED_BYTES[slot];
  return [bytes[0] / 255, bytes[1] / 255, bytes[2] / 255];
}

export function chainsForJoints(jointNames) {
  const have = new Set(jointNames || []);
  return SCHEME_CHAINS.filter((chain) => chain.every((name) => have.has(name)));
}

export function bonesFromChains(jointNames, chains) {
  const index = new Map((jointNames || []).map((name, i) => [name, i]));
  const bones = [];
  const boneColors = [];
  (chains || []).forEach((chain, chainIndex) => {
    const color = displayedTab10(chainIndex);
    for (let i = 0; i < chain.length - 1; i += 1) {
      const a = index.get(chain[i]);
      const b = index.get(chain[i + 1]);
      if (a == null || b == null || a === b) continue;
      bones.push([a, b]);
      boneColors.push(color);
    }
  });
  return { bones, boneColors };
}

export function normalizeRgb(value) {
  if (!Array.isArray(value) || value.length < 3) return [1, 1, 1];
  const r = Number(value[0]);
  const g = Number(value[1]);
  const b = Number(value[2]);
  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) {
    return [1, 1, 1];
  }
  const scale = Math.max(r, g, b) > 1 ? 255 : 1;
  return [r / scale, g / scale, b / scale];
}

function finiteOr(value, fallback) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function normalizeFloor(floor) {
  const src = floor && typeof floor === 'object' ? floor : {};
  let minX = finiteOr(src.minX, finiteOr(src.x0, -1.5));
  let maxX = finiteOr(src.maxX, finiteOr(src.x1, 1.5));
  let minY = finiteOr(src.minY, finiteOr(src.y0, -1.5));
  let maxY = finiteOr(src.maxY, finiteOr(src.y1, 1.5));
  if (!(maxX > minX) || !(maxY > minY)) {
    minX = -1.5;
    maxX = 1.5;
    minY = -1.5;
    maxY = 1.5;
  }
  return { minX, maxX, minY, maxY };
}

export function normalizeLiveMeta(raw) {
  if (!raw || typeof raw !== 'object') {
    throw new Error('live layout meta missing');
  }
  const frameCount = Number(raw.frameCount);
  if (!Number.isFinite(frameCount) || frameCount < 1) {
    throw new Error('live layout frameCount missing');
  }
  const jointNames = Array.isArray(raw.jointNames) ? raw.jointNames.map(String) : null;
  if (!jointNames || jointNames.length < 1) {
    throw new Error('live layout jointNames missing');
  }
  if (!raw.poseUrl) {
    throw new Error('live layout poseUrl missing');
  }

  let bones = [];
  if (Array.isArray(raw.bones)) {
    bones = raw.bones
      .filter((pair) => Array.isArray(pair) && pair.length >= 2)
      .map((pair) => [Number(pair[0]), Number(pair[1])])
      .filter((pair) => pair.every((n) => Number.isFinite(n)));
  }
  let boneColors = Array.isArray(raw.boneColors) ? raw.boneColors.map(normalizeRgb) : [];
  if (bones.length === 0) {
    const built = bonesFromChains(jointNames, chainsForJoints(jointNames));
    bones = built.bones;
    boneColors = built.boneColors;
  } else if (boneColors.length < bones.length) {
    for (let i = boneColors.length; i < bones.length; i += 1) {
      boneColors.push(displayedTab10(i));
    }
  } else if (boneColors.length > bones.length) {
    boneColors = boneColors.slice(0, bones.length);
  }

  const fps = Number(raw.fps);
  const cameras = (Array.isArray(raw.cameras) ? raw.cameras : [])
    .map((cam, i) => ({
      label: cam && cam.label ? String(cam.label) : `cam${i}`,
      name: cameraName(cam, i),
      src: cameraPlaybackSrc(cam),
      file: cam && cam.file ? String(cam.file) : '',
    }))
    .filter((cam) => cam.src);

  return {
    fps: Number.isFinite(fps) && fps > 0 ? fps : LIVE_FPS,
    frameCount: Math.floor(frameCount),
    jointNames,
    bones,
    boneColors,
    floor: normalizeFloor(raw.floor),
    cameras,
    poseUrl: String(raw.poseUrl),
    reprojectionUrl: raw.reprojectionUrl
      ? String(raw.reprojectionUrl)
      : (raw.reprojection_url ? String(raw.reprojection_url) : ''),
  };
}

export function parsePoseBuffer(buffer, frameCount, jointCount) {
  const expected = frameCount * jointCount * 3;
  if (!buffer || typeof buffer.byteLength !== 'number') {
    throw new Error('pose buffer missing');
  }
  if (buffer.byteLength < expected * 4) {
    throw new Error('pose buffer short');
  }
  return new Float32Array(buffer, 0, expected);
}

function decodedSource(value) {
  const text = String(value || '');
  try {
    return decodeURIComponent(text);
  } catch (err) {
    return text;
  }
}

/** Raw GoPro files. The live stage plays these and draws bones on top. */
export function isOriginalCameraSource(value) {
  return /videos-raw|videos_raw|(^|[/?=&])raw([/?=&]|$)|original/i.test(decodedSource(value));
}

/** Baked plot_all MP4s. The live stage must not play these. */
export function isOverlayCameraSource(value) {
  return /overlay|plot_all|reproject|(^|\/)cams\/cam\d/i.test(decodedSource(value));
}

function mediaStem(value) {
  if (!value || isOverlayCameraSource(value)) return '';
  const path = String(value).split(/[?#]/)[0];
  const base = path.split(/[/\\]/).pop() || '';
  return base.replace(/\.(mp4|webm|mov)$/i, '');
}

/** Stem that matches reprojection `cameras[].name` and `videos-raw/{trial}/{name}.mp4`. */
export function cameraName(cam, index = 0) {
  if (!cam || typeof cam !== 'object') return `cam${index}`;
  if (cam.name) return String(cam.name);
  return mediaStem(cam.file) || mediaStem(cam.src) || (cam.label ? String(cam.label) : `cam${index}`);
}

function playableCameraSource(value) {
  if (!value || isOverlayCameraSource(value)) return '';
  const text = String(value);
  if (isOriginalCameraSource(text) || /^https?:\/\//i.test(text) || text.startsWith('/')) return text;
  return '';
}

/**
 * Playback URL for one camera. The original file is the picture.
 * Baked overlay URLs, including overlaySrc, are ignored.
 */
export function cameraPlaybackSrc(cam) {
  if (!cam || typeof cam !== 'object') return '';
  const kind = String(cam.kind || cam.type || '').toLowerCase();
  if (kind === 'overlay' || kind === '2d') return '';
  return playableCameraSource(cam.src) || playableCameraSource(cam.file);
}

export function resolvePoseUrl(poseUrl, serverUrl) {
  if (/^https?:\/\//i.test(poseUrl)) return poseUrl;
  const base = serverUrl || '';
  if (poseUrl.startsWith('/')) return `${base}${poseUrl}`;
  return `${base}/${poseUrl}`;
}

/** Camera rate for the pictures on screen. Pose fps is only a fallback. */
export function playbackFps(meta, reprojection) {
  const tracks = reprojection && Array.isArray(reprojection.cameras) ? reprojection.cameras : [];
  for (let i = 0; i < tracks.length; i += 1) {
    const rate = tracks[i] && Number(tracks[i].fps);
    if (Number.isFinite(rate) && rate > 1) return rate;
  }
  const pose = meta && Number(meta.fps);
  if (Number.isFinite(pose) && pose > 1) return pose;
  return LIVE_FPS;
}

/** How many camera frames the scrub clock may address. */
export function videoFrameCount(meta, reprojection, fps) {
  const tracks = reprojection && Array.isArray(reprojection.cameras) ? reprojection.cameras : [];
  let count = 0;
  for (let i = 0; i < tracks.length; i += 1) {
    const xy = tracks[i] && tracks[i].xy;
    if (Array.isArray(xy) && xy.length > count) count = xy.length;
  }
  if (count > 0) return count;
  const poseFrames = meta && Number(meta.frameCount);
  const poseFps = meta && Number(meta.fps);
  const rate = Number.isFinite(fps) && fps > 1 ? fps : poseFps;
  if (Number.isFinite(poseFrames) && poseFrames > 0 && Number.isFinite(poseFps) && poseFps > 0 && Number.isFinite(rate) && rate > 0) {
    return Math.max(1, Math.round((poseFrames * rate) / poseFps));
  }
  return Number.isFinite(poseFrames) && poseFrames > 0 ? Math.floor(poseFrames) : 1;
}

/**
 * Integer frame for a media clock. Floor, not round: the second half of a
 * frame must not count as the next one. A tiny epsilon absorbs binary
 * error so 6.05s at 60fps is frame 363, not 362.
 */
export function playbackFrameNumber(mediaTime, fps) {
  const rate = Number.isFinite(fps) && fps > 0 ? fps : 0;
  if (rate === 0 || !Number.isFinite(mediaTime) || mediaTime < 0) return 0;
  return Math.floor(mediaTime * rate + 1e-6);
}

/**
 * Playback rate that walks a slave onto the master's presented frame without
 * a seek (a seek during play freezes the picture). `behind` is master frame
 * minus slave frame. Returns null when the gap is too wide to trim.
 */
export function slaveTrimRate(behind, maxFrames = 12) {
  if (!Number.isFinite(behind) || behind === 0) return 1;
  if (Math.abs(behind) > maxFrames) return null;
  return 1 + Math.max(-0.25, Math.min(0.25, behind * 0.05));
}

/**
 * Frames a playing group trails real time since `anchor` ({ wallMs, frame }),
 * the wall clock and frame recorded when play started.
 */
export function framesBehindWallClock(anchor, nowMs, fps, frame) {
  if (!anchor || !Number.isFinite(anchor.wallMs) || !Number.isFinite(anchor.frame)) return 0;
  if (!Number.isFinite(nowMs) || !Number.isFinite(fps) || fps <= 0 || !Number.isFinite(frame)) return 0;
  return anchor.frame + ((nowMs - anchor.wallMs) * fps) / 1000 - frame;
}

/** Media time inside one camera frame, away from the rounding boundary. */
export function mediaTimeForFrame(frame, fps) {
  const rate = Number.isFinite(fps) && fps > 0 ? fps : LIVE_FPS;
  const index = Number.isFinite(frame) ? Math.max(0, Math.round(frame)) : 0;
  return (index + 0.25) / rate;
}

export function frameFromMediaTime(mediaTime, fps, frameCount) {
  const count = Number.isFinite(frameCount) ? Math.max(0, Math.floor(frameCount)) : 0;
  if (count < 1) return 0;
  const rate = Number.isFinite(fps) && fps > 0 ? fps : 0;
  if (rate === 0 || !Number.isFinite(mediaTime)) return 0;
  const raw = Math.round(mediaTime * rate);
  if (raw < 0) return 0;
  if (raw > count - 1) return count - 1;
  return raw;
}

/**
 * A scrub time moved a quarter frame forward, so every camera is asked for
 * the inside of the same frame instead of a rounding boundary.
 */
export function snapMediaTime(mediaTime, fps, frameCount) {
  const rate = Number.isFinite(fps) && fps > 0 ? fps : LIVE_FPS;
  const frame = frameFromMediaTime(mediaTime, rate, frameCount);
  return (frame + 0.25) / rate;
}

/**
 * Seconds to add so a camera showing `presented` steps onto `target`.
 * Limited to two frames so a bad read cannot throw the playhead.
 */
export function frameAlignDelta(presented, target, fps) {
  if (!Number.isFinite(presented) || !Number.isFinite(target) || presented === target) return 0;
  const rate = Number.isFinite(fps) && fps > 0 ? fps : LIVE_FPS;
  const steps = Math.max(-2, Math.min(2, target - presented));
  return steps / rate;
}

/** True when a slave is more than one frame away from the master clock. */
export function slaveNeedsSeek(slaveTime, masterTime, fps) {
  if (!Number.isFinite(slaveTime) || !Number.isFinite(masterTime)) return false;
  const rate = Number.isFinite(fps) && fps > 0 ? fps : LIVE_FPS;
  return Math.abs(slaveTime - masterTime) > 1 / rate;
}

export function formatPlaybackReadout(frame, mediaTime) {
  const f = Number.isFinite(frame) ? frame : 0;
  const t = Number.isFinite(mediaTime) ? mediaTime : 0;
  return `Frame ${f} · ${t.toFixed(2)}s`;
}

/**
 * Cameras alternate left and right of the skeleton, top to bottom. Five slots
 * always render so short rigs keep the stage shape; larger rigs (legacy trials
 * have six cameras) get one slot per camera.
 */
export function cameraGridSlots(cameras) {
  const list = Array.isArray(cameras) ? cameras : [];
  const count = Math.max(5, list.length);
  return Array.from({ length: count }, (_, i) => ({
    key: `cam-${i}`,
    column: i % 2 === 0 ? 1 : 3,
    row: Math.floor(i / 2) + 1,
    camera: list[i] || null,
  }));
}

export function floorLookAt(floor) {
  return {
    x: (floor.minX + floor.maxX) / 2,
    y: (floor.minY + floor.maxY) / 2,
    z: LOOK_AT_Z,
  };
}

export function fitDistance(floor, fovDeg = VERTICAL_FOV_DEG) {
  const span = Math.max(floor.maxX - floor.minX, floor.maxY - floor.minY, 0.5);
  const degrees = Number.isFinite(fovDeg) && fovDeg > 1 ? fovDeg : VERTICAL_FOV_DEG;
  const half = (degrees * Math.PI) / 360;
  const distance = (span * 0.65) / Math.tan(half);
  return Math.min(24, Math.max(2.5, distance));
}

export const MIN_JOINT_DIAMETER_CSS_PX = 2.5;
export const MIN_BONE_THICKNESS_CSS_PX = 0.75;
/** Real-world joint ball and bone widths, so marks keep the body's proportions. */
export const JOINT_DIAMETER_M = 0.07;
export const BONE_THICKNESS_M = 0.016;

/** CSS pixels one meter covers at the look-at point. */
export function pixelsPerMeter(distance, cssHeight, fovDeg = VERTICAL_FOV_DEG) {
  if (!Number.isFinite(distance) || distance <= 0 || !Number.isFinite(cssHeight) || cssHeight <= 0) return NaN;
  const half = (fovDeg * Math.PI) / 360;
  return cssHeight / (2 * distance * Math.tan(half));
}

/**
 * Joint and bone sizes in CSS pixels. Both follow the body's on-screen
 * scale, so a small figure (zoomed out, or a large floor) gets small joints.
 * They never grow past the full size, and never shrink below a visible floor.
 */
export function skeletonMarkSizes(distance, cssHeight) {
  const scale = pixelsPerMeter(distance, cssHeight);
  const fit = (meters, min, max) => (Number.isFinite(scale)
    ? Math.min(max, Math.max(min, meters * scale))
    : max);
  const jointDiameterCss = fit(JOINT_DIAMETER_M, MIN_JOINT_DIAMETER_CSS_PX, JOINT_DIAMETER_CSS_PX);
  return {
    jointDiameterCss,
    jointRadiusCss: jointDiameterCss / 2,
    boneThicknessCss: fit(BONE_THICKNESS_M, MIN_BONE_THICKNESS_CSS_PX, BONE_THICKNESS_CSS_PX),
  };
}

export function cameraEye(target, azimuth, elevation, distance) {
  const cosE = Math.cos(elevation);
  return [
    target.x + distance * cosE * Math.cos(azimuth),
    target.y + distance * cosE * Math.sin(azimuth),
    target.z + distance * Math.sin(elevation),
  ];
}

export function clampElevation(elevation) {
  if (!Number.isFinite(elevation)) return DEFAULT_ELEVATION;
  return Math.min(1.2, Math.max(-1.2, elevation));
}

export function clampDistance(distance) {
  if (!Number.isFinite(distance)) return 6;
  return Math.min(26, Math.max(1.2, distance));
}

export function zoomDistance(distance, deltaY) {
  const factor = deltaY > 0 ? 1.08 : 0.92;
  return clampDistance(distance * factor);
}

export function orbitDrag(azimuth, elevation, dx, dy) {
  return {
    azimuth: azimuth - dx * 0.005,
    elevation: clampElevation(elevation + dy * 0.005),
  };
}

/** Floor look-at plus a pan offset (metres). */
export function cameraTarget(floor, pan) {
  const base = floorLookAt(floor);
  const px = pan && Number.isFinite(pan.x) ? pan.x : 0;
  const py = pan && Number.isFinite(pan.y) ? pan.y : 0;
  const pz = pan && Number.isFinite(pan.z) ? pan.z : 0;
  return { x: base.x + px, y: base.y + py, z: base.z + pz };
}

/**
 * Pan the orbit target in screen space. Dragging right moves the scene with
 * the pointer (target moves left). Clamps the offset so the look-at stays
 * near the floor bounds.
 */
export function panTarget(pan, azimuth, elevation, distance, dx, dy, cssHeight, floor) {
  const prev = {
    x: pan && Number.isFinite(pan.x) ? pan.x : 0,
    y: pan && Number.isFinite(pan.y) ? pan.y : 0,
    z: pan && Number.isFinite(pan.z) ? pan.z : 0,
  };
  const scale = pixelsPerMeter(distance, cssHeight);
  if (!Number.isFinite(scale) || scale <= 0
    || !Number.isFinite(dx) || !Number.isFinite(dy)
    || !Number.isFinite(azimuth) || !Number.isFinite(elevation)) {
    return prev;
  }
  const mpp = 1 / scale;
  const cosE = Math.cos(elevation);
  const sinE = Math.sin(elevation);
  const forward = [
    -cosE * Math.cos(azimuth),
    -cosE * Math.sin(azimuth),
    -sinE,
  ];
  let right = cross3(forward, [0, 0, 1]);
  if (len3(right) < 1e-8) {
    // Looking straight up/down: world-up × forward is degenerate.
    right = [-Math.sin(azimuth), Math.cos(azimuth), 0];
  } else {
    right = norm3(right);
  }
  const up = norm3(cross3(right, forward));
  const next = {
    x: prev.x - right[0] * dx * mpp + up[0] * dy * mpp,
    y: prev.y - right[1] * dx * mpp + up[1] * dy * mpp,
    z: prev.z - right[2] * dx * mpp + up[2] * dy * mpp,
  };
  if (floor && typeof floor === 'object') {
    const bounds = normalizeFloor(floor);
    const span = Math.max(
      bounds.maxX - bounds.minX,
      bounds.maxY - bounds.minY,
      1
    );
    const margin = span * 0.5;
    const minX = bounds.minX - margin;
    const maxX = bounds.maxX + margin;
    const minY = bounds.minY - margin;
    const maxY = bounds.maxY + margin;
    const center = floorLookAt(bounds);
    next.x = Math.min(maxX - center.x, Math.max(minX - center.x, next.x));
    next.y = Math.min(maxY - center.y, Math.max(minY - center.y, next.y));
    next.z = Math.min(2, Math.max(-0.5, next.z));
  }
  return next;
}

function sub3(a, b) {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function dot3(a, b) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function cross3(a, b) {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function len3(a) {
  return Math.hypot(a[0], a[1], a[2]);
}

function norm3(a) {
  const l = len3(a);
  if (l < 1e-8) return [0, 0, 0];
  return [a[0] / l, a[1] / l, a[2] / l];
}

export function mat4Perspective(fovyRad, aspect, near, far) {
  const f = 1 / Math.tan(fovyRad / 2);
  const nf = 1 / (near - far);
  const out = new Float32Array(16);
  out[0] = f / aspect;
  out[5] = f;
  out[10] = (far + near) * nf;
  out[11] = -1;
  out[14] = 2 * far * near * nf;
  return out;
}

export function mat4LookAt(eye, target, up) {
  let forward = norm3(sub3(target, eye));
  if (len3(forward) < 1e-8) forward = [0, 1, 0];
  let side = norm3(cross3(forward, up));
  if (len3(side) < 1e-8) side = norm3(cross3(forward, [0, 1, 0]));
  if (len3(side) < 1e-8) side = norm3(cross3(forward, [1, 0, 0]));
  const lifted = cross3(side, forward);
  const out = new Float32Array(16);
  out[0] = side[0];
  out[4] = side[1];
  out[8] = side[2];
  out[12] = -dot3(side, eye);
  out[1] = lifted[0];
  out[5] = lifted[1];
  out[9] = lifted[2];
  out[13] = -dot3(lifted, eye);
  out[2] = -forward[0];
  out[6] = -forward[1];
  out[10] = -forward[2];
  out[14] = dot3(forward, eye);
  out[15] = 1;
  return out;
}

export function mat4Multiply(a, b) {
  const out = new Float32Array(16);
  for (let col = 0; col < 4; col += 1) {
    for (let row = 0; row < 4; row += 1) {
      out[col * 4 + row] =
        a[row] * b[col * 4] +
        a[4 + row] * b[col * 4 + 1] +
        a[8 + row] * b[col * 4 + 2] +
        a[12 + row] * b[col * 4 + 3];
    }
  }
  return out;
}

export function viewProjectionMatrix(eye, target, aspect) {
  const safeAspect = aspect > 0 ? aspect : 1;
  const proj = mat4Perspective(
    (VERTICAL_FOV_DEG * Math.PI) / 180,
    safeAspect,
    CAMERA_NEAR,
    CAMERA_FAR
  );
  const view = mat4LookAt(eye, [target.x, target.y, target.z], [0, 0, 1]);
  return mat4Multiply(proj, view);
}

export function projectToNdc(viewProj, x, y, z) {
  const m = viewProj;
  const cx = m[0] * x + m[4] * y + m[8] * z + m[12];
  const cy = m[1] * x + m[5] * y + m[9] * z + m[13];
  const cz = m[2] * x + m[6] * y + m[10] * z + m[14];
  const cw = m[3] * x + m[7] * y + m[11] * z + m[15];
  if (!Number.isFinite(cw) || cw <= 1e-6) return null;
  return { x: cx / cw, y: cy / cw, z: cz / cw };
}

export function cssFromNdc(ndcX, ndcY, cssWidth, cssHeight) {
  return {
    x: (ndcX * 0.5 + 0.5) * cssWidth,
    y: (ndcY * -0.5 + 0.5) * cssHeight,
  };
}

const BONE_QUAD_FLOATS = 18;

/**
 * Writes one screen-space quad into `out` as 6 vertices of NDC x, y, z.
 * Both ends share a single perpendicular so the limb does not twist. The
 * segment stops at the joint radius, and NDC z is pulled slightly closer
 * than the true depth. Returns the float count written, or 0 when skipped.
 */
export function writeBoneQuad(
  out,
  offset,
  axCss,
  ayCss,
  bxCss,
  byCss,
  aNdcZ,
  bNdcZ,
  cssWidth,
  cssHeight,
  thicknessCss = BONE_THICKNESS_CSS_PX,
  jointRadiusCss = JOINT_RADIUS_CSS_PX
) {
  if (!out || offset < 0 || offset + BONE_QUAD_FLOATS > out.length) return 0;
  if (!(cssWidth > 0) || !(cssHeight > 0)) return 0;
  if (
    !Number.isFinite(axCss) ||
    !Number.isFinite(ayCss) ||
    !Number.isFinite(bxCss) ||
    !Number.isFinite(byCss) ||
    !Number.isFinite(aNdcZ) ||
    !Number.isFinite(bNdcZ)
  ) {
    return 0;
  }
  const dx = bxCss - axCss;
  const dy = byCss - ayCss;
  const length = Math.hypot(dx, dy);
  if (!(length > jointRadiusCss * 2)) return 0;
  const dirX = dx / length;
  const dirY = dy / length;
  const ax = axCss + dirX * jointRadiusCss;
  const ay = ayCss + dirY * jointRadiusCss;
  const bx = bxCss - dirX * jointRadiusCss;
  const by = byCss - dirY * jointRadiusCss;
  const perpX = -dirY;
  const perpY = dirX;
  const half = thicknessCss / 2;
  const zA = aNdcZ - DEPTH_PULL_NDC;
  const zB = bNdcZ - DEPTH_PULL_NDC;
  const corner = (slot, x, y, z) => {
    const o = offset + slot;
    out[o] = (x / cssWidth) * 2 - 1;
    out[o + 1] = 1 - (y / cssHeight) * 2;
    out[o + 2] = z;
  };
  corner(0, ax + perpX * half, ay + perpY * half, zA);
  corner(3, ax - perpX * half, ay - perpY * half, zA);
  corner(6, bx - perpX * half, by - perpY * half, zB);
  corner(9, ax + perpX * half, ay + perpY * half, zA);
  corner(12, bx - perpX * half, by - perpY * half, zB);
  corner(15, bx + perpX * half, by + perpY * half, zB);
  return BONE_QUAD_FLOATS;
}

/**
 * One screen-space quad. Both ends share a single perpendicular so the limb
 * does not twist. The segment stops at the joint radius, and NDC z is pulled
 * slightly closer than the true depth.
 */
export function screenBoneQuad(
  aCss,
  bCss,
  aNdcZ,
  bNdcZ,
  cssWidth,
  cssHeight,
  thicknessCss = BONE_THICKNESS_CSS_PX,
  jointRadiusCss = JOINT_RADIUS_CSS_PX
) {
  const buf = new Float32Array(BONE_QUAD_FLOATS);
  const written = writeBoneQuad(
    buf,
    0,
    aCss && aCss.x,
    aCss && aCss.y,
    bCss && bCss.x,
    bCss && bCss.y,
    aNdcZ,
    bNdcZ,
    cssWidth,
    cssHeight,
    thicknessCss,
    jointRadiusCss
  );
  if (written !== BONE_QUAD_FLOATS) return null;
  const verts = [];
  for (let i = 0; i < BONE_QUAD_FLOATS; i += 3) {
    verts.push({ x: buf[i], y: buf[i + 1], z: buf[i + 2] });
  }
  return verts;
}

export function readJoint(pose, frame, joint, jointCount) {
  if (!pose || frame < 0 || joint < 0) return null;
  const o = (frame * jointCount + joint) * 3;
  if (o < 0 || o + 2 >= pose.length) return null;
  const x = pose[o];
  const y = pose[o + 1];
  const z = pose[o + 2];
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return null;
  return [x, y, z];
}

const TORSO_JOINTS = ['hip_left', 'hip_right', 'shoulder_left', 'shoulder_right'];

/**
 * Horizontal mediolateral / anterior unit vectors from the standing start
 * of a trial (average of the first valid hip+shoulder samples).
 */
export function torsoAxes(pose, jointNames, jointCount, frameCount, sampleFrames = 15) {
  if (!pose || !Array.isArray(jointNames) || !Number.isFinite(jointCount) || jointCount < 1) {
    return null;
  }
  const index = new Map(jointNames.map((name, i) => [name, i]));
  if (!TORSO_JOINTS.every((name) => index.has(name))) return null;
  const count = Number.isFinite(frameCount) ? Math.max(0, Math.floor(frameCount)) : 0;
  const samples = Math.max(1, Math.min(
    Number.isFinite(sampleFrames) ? Math.floor(sampleFrames) : 15,
    count || 1
  ));
  const sums = {
    hip_left: [0, 0, 0],
    hip_right: [0, 0, 0],
    shoulder_left: [0, 0, 0],
    shoulder_right: [0, 0, 0],
  };
  let used = 0;
  for (let frame = 0; frame < samples; frame += 1) {
    const points = TORSO_JOINTS.map((name) => readJoint(pose, frame, index.get(name), jointCount));
    if (points.some((p) => !p)) continue;
    used += 1;
    for (let i = 0; i < TORSO_JOINTS.length; i += 1) {
      const name = TORSO_JOINTS[i];
      sums[name][0] += points[i][0];
      sums[name][1] += points[i][1];
      sums[name][2] += points[i][2];
    }
  }
  if (used < 1) return null;
  const avg = (name) => [
    sums[name][0] / used,
    sums[name][1] / used,
    sums[name][2] / used,
  ];
  const left = [
    (avg('hip_left')[0] + avg('shoulder_left')[0]) / 2,
    (avg('hip_left')[1] + avg('shoulder_left')[1]) / 2,
    (avg('hip_left')[2] + avg('shoulder_left')[2]) / 2,
  ];
  const right = [
    (avg('hip_right')[0] + avg('shoulder_right')[0]) / 2,
    (avg('hip_right')[1] + avg('shoulder_right')[1]) / 2,
    (avg('hip_right')[2] + avg('shoulder_right')[2]) / 2,
  ];
  const mlFlat = [right[0] - left[0], right[1] - left[1], 0];
  const ml = norm3(mlFlat);
  if (len3(ml) < 1e-8) return null;
  // ml is horizontal and unit-length, so z-up × ml is a unit anterior.
  const anterior = norm3(cross3([0, 0, 1], ml));
  return { mediolateral: ml, anterior };
}

/** Orbit azimuth that places the camera on a clinical plane view. */
export function planeViewAzimuth(axes, plane) {
  if (!axes || typeof axes !== 'object') return null;
  const dir = plane === 'sagittal' ? axes.mediolateral : plane === 'frontal' ? axes.anterior : null;
  if (!dir || !Number.isFinite(dir[0]) || !Number.isFinite(dir[1])) return null;
  if (Math.hypot(dir[0], dir[1]) < 1e-8) return null;
  return Math.atan2(dir[1], dir[0]);
}
